import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, X } from 'lucide-react';

/**
 * Full-screen image preview, opened by clicking any `AttachmentImage`.
 *
 * Before this, a photo in a comment or a chat bubble was wrapped in an
 * `<a target="_blank">`, so looking at a screenshot meant leaving the task for
 * a raw browser tab. The lightbox keeps the reader where they are.
 *
 * Rendered into a portal at `document.body` — same as `BottomSheet` and
 * `Popover` — so the overlay is never clipped by the scroll containers of the
 * task detail panel or the chat pane, and `position: fixed` is not broken by a
 * transformed ancestor. Body scroll is locked with the same save/restore
 * pattern, so opening a lightbox from inside a sheet composes correctly.
 *
 * The backdrop stays a static black scrim in both themes (`bg-black/80`, which
 * tailwind.config.js keeps static exactly for overlays), while the chrome uses
 * the theme-mapped ramps.
 */
export function ImageLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Stop the task detail panel / sheet behind us from closing too.
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // z-[95] sits above every other layer in the app (sheets 50/60, hover card
  // 70, toasts 80, dialogs 90): a picture can be opened from inside any of
  // them, and the preview is always the thing the reader just asked for.
  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || 'Previzualizare imagine'}
    >
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Deschide originalul într-un tab nou"
          className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
        >
          <ExternalLink size={14} /> Deschide originalul
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Închide previzualizarea"
          className="inline-flex items-center rounded-md bg-gray-100 p-1.5 text-gray-700 hover:bg-gray-200"
        >
          <X size={16} />
        </button>
      </div>
      {/* Clicking the picture itself must not dismiss — only the backdrop does. */}
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] cursor-default rounded-md object-contain shadow-xl"
      />
    </div>,
    document.body,
  );
}
